import React from 'react';
import { Code, Server, Cloud, Wrench, Database } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePortfolio } from '@/context/PortfolioContext';
import {
  SectionShell,
  SectionInner,
  SectionHeader,
  SectionLoading,
  SectionError,
} from '@/components/section/sectionLayout';

const Skills = () => {
  const { portfolioData, loading, error } = usePortfolio();

  if (loading) return <SectionLoading id="skills" />;
  if (error) return <SectionError id="skills" error={error} />;

  const skills = portfolioData?.skills || {};

  const categories = [
    { id: 'frontend', label: 'Frontend', shortLabel: 'Front', icon: Code, data: skills.frontend || [] },
    { id: 'backend', label: 'Backend', shortLabel: 'Back', icon: Server, data: skills.backend || [] },
    { id: 'database', label: 'Base de données', shortLabel: 'BDD', icon: Database, data: skills.database || [] },
    { id: 'devops', label: 'DevOps', shortLabel: 'DevOps', icon: Cloud, data: skills.devops || [] },
    { id: 'data', label: 'Data', shortLabel: 'Data', icon: Code, data: skills.data || [] },
    { id: 'tools', label: 'Outils', shortLabel: 'Tools', icon: Wrench, data: skills.tools || [] },
  ];

  return (
    <SectionShell id="skills">
      <SectionInner>
        <SectionHeader title="Compétences" highlight="techniques" />

        <Tabs defaultValue="frontend" className="w-full">
          <div className="-mx-4 mb-8 overflow-x-auto px-4 sm:mx-0 sm:px-0 lg:mb-12">
            <TabsList className="inline-flex h-auto min-w-full gap-2 bg-white p-2 dark:bg-[#1a1f2e] sm:grid sm:w-full sm:grid-cols-3 sm:gap-2 lg:grid-cols-6">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className="shrink-0 px-3 py-2 text-xs data-[state=active]:bg-emerald-500 data-[state=active]:text-white sm:text-sm"
                  >
                    <Icon className="mr-1.5 h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">{category.label}</span>
                    <span className="sm:hidden">{category.shortLabel}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {categories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-0 space-y-4">
              {category.data.length === 0 ? (
                <p className="py-8 text-center text-gray-500 dark:text-gray-400">
                  Aucune compétence dans cette catégorie.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                  {category.data.map((skill, index) => (
                    <Card
                      key={skill._id || skill.id || `${category.id}-${skill.name}-${index}`}
                      className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]"
                    >
                      <CardContent className="p-4 sm:p-6">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                            {skill.name}
                          </h3>
                          <span className="shrink-0 text-sm font-bold text-emerald-500 sm:text-base">
                            {skill.level}%
                          </span>
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-[#0f1419]">
                          <div
                            className="h-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
                            style={{ width: `${skill.level}%` }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </SectionInner>
    </SectionShell>
  );
};

export default Skills;
